import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState({});

  useEffect(() => {
    if (!sid) { setStatus("missing"); return; }
    let tries = 0;
    let timer;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setInfo(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch (e) { console.debug("payment status poll retry", e); }
      if (++tries < 8) timer = setTimeout(tick, 2000);
      else setStatus("timeout");
    };
    tick();
    return () => timer && clearTimeout(timer);
  }, [sid]);

  const isPlan = info.kind === "subscription";
  const isBump = info.kind === "bump";
  const isBarter = info.kind === "barter_fee";
  const bumpHours = Number(info.hours) || 5;
  const isSpotlight = info.tier === "spotlight48" || bumpHours >= 24;
  const bumpLabel = isSpotlight ? "48-hour spotlight" : "Homepage feature";
  const bumpDuration = bumpHours >= 24 ? `${bumpHours / 24} day${bumpHours / 24 === 1 ? "" : "s"}` : `${bumpHours} hours`;

  return (
    <div data-testid="success-page" className="max-w-[600px] mx-auto px-6 py-24 text-center">
      <CheckCircle2 size={48} strokeWidth={1.2} style={{color:"#2563EB"}} className="mx-auto"/>
      <div className="label-eyebrow mt-6">{isPlan ? "Seller plan" : isBump ? bumpLabel : isBarter ? "Barter fee" : "Order"}</div>
      <h1 className="font-display text-4xl tracking-tighter mt-2">
        {status === "paid"
          ? (isPlan ? "You're upgraded." : isBump ? "You're featured." : isBarter ? "Barter fee paid." : "Thank you.")
          : status === "checking" ? "Confirming payment…" : "Payment status pending"}
      </h1>
      <p className="mt-4 text-neutral-500 text-sm">
        {status === "paid"
          ? (isPlan
              ? `Your ${info.plan || "premium"} tools are live for the next 30 days — unlimited listings, private offers and priority placement.`
              : isBump
              ? `Your listing is now pinned to the homepage “For Sale” grid for the next ${bumpDuration}.`
              : isBarter
              ? "Your $2 barter fee is paid. Once both sides have paid, we'll unlock each other's shipping address so you can send your items."
              : "Your order has been placed and sellers have been notified. Track delivery from Purchases.")
          : "We're checking with Stripe. This normally takes just a moment."}
      </p>
      <div className="mt-8 flex gap-3 justify-center">
        {isPlan ? (
          <>
            <Link to="/dashboard" className="btn-primary" data-testid="success-dashboard">Seller dashboard</Link>
            <Link to="/sell" className="btn-outline">List something</Link>
          </>
        ) : isBump ? (
          <>
            {info.listing_id && <Link to={`/product/${info.listing_id}`} className="btn-primary" data-testid="success-listing">View my listing</Link>}
            <Link to="/dashboard" className="btn-outline" data-testid="success-dashboard">Seller dashboard</Link>
          </>
        ) : isBarter ? (
          <>
            <Link to="/trades" className="btn-primary" data-testid="success-trades">Back to my trades</Link>
            <Link to="/browse" className="btn-outline">Keep shopping</Link>
          </>
        ) : (
          <>
            <Link to="/orders" className="btn-primary" data-testid="success-orders">Track my order</Link>
            <Link to="/browse" className="btn-outline">Keep shopping</Link>
          </>
        )}
      </div>
    </div>
  );
}
